import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { helpTextCls, inputCls, labelCls } from "components/tw/classes";
import {
  testFinvaldaConnection,
  updateFinvaldaSettings,
} from "services/company.service";
import { CompanyEntity } from "types/Company";
import { formatPrettyDate } from "utils/helperFunctions";
import {
  ChangeLink,
  EditPanel,
  SECONDARY_BUTTON,
  SettingRow,
  SettingsCard,
  StatusBanner,
  Toggle,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";
import FinvaldaConnectionCard from "./FinvaldaConnectionCard";
import LedgerJournalsCard from "./LedgerJournalsCard";
import {
  JOURNAL_GROUP_FORMAT,
  SaveLock,
  finvaldaSaveErrorKey,
  isFinvaldaConnectionSaved,
  storedFinvaldaRequest,
  useSaveLock,
} from "./finvalda-request";

// What a failed connection check says, by the reason the server gives. Any
// other reason, or no answer at all, gets the last line.
const CHECK_FAILURE_TEXT_KEYS: Record<string, string> = {
  NOT_CONFIGURED: "settings_finvalda_check_not_configured",
  ADDRESS_NOT_ALLOWED: "settings_finvalda_address_not_allowed",
  ACCESS_DENIED: "settings_finvalda_check_access_denied",
  TIMEOUT: "settings_finvalda_check_timeout",
  UNREACHABLE: "settings_finvalda_check_unreachable",
};
const CHECK_FAILED_TEXT_KEY = "settings_finvalda_check_failed";

type CheckResult = { ok: true } | { ok: false; reason: string };

/** Whether data is arriving from Finvalda, with a button that tries the saved login right now. */
function SyncStatus({ company }: { company: CompanyEntity }) {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  // A result only says something about the login it was made with.
  const login = [
    company.finvaldaAddress,
    company.finvaldaUser,
    company.finvaldaCompanyId,
    company.finvaldaPass,
  ].join("|");
  const [checked, setChecked] = useState<{
    login: string;
    result: CheckResult;
  } | null>(null);
  const result = checked?.login === login ? checked.result : null;

  const check = async () => {
    setChecking(true);
    setChecked(null);
    try {
      setChecked({
        login,
        result: await testFinvaldaConnection(company.id),
      });
    } catch {
      // Our own server did not answer; the server never sends this reason.
      setChecked({
        login,
        result: { ok: false, reason: "REQUEST_FAILED" },
      });
    } finally {
      setChecking(false);
    }
  };

  const lastSync = company.finvaldaSyncDate
    ? formatPrettyDate(company.finvaldaSyncDate, "lt-LT", true)
    : t("settings_finvalda_never_synced");

  return (
    <div className="space-y-3">
      <StatusBanner
        tone={company.isFinvaldaSyncActive ? "ok" : "info"}
        title={t(
          company.isFinvaldaSyncActive
            ? "settings_finvalda_sync_on"
            : "settings_finvalda_sync_off"
        )}
        detail={
          company.isFinvaldaSyncActive
            ? t("settings_finvalda_last_sync", { when: lastSync })
            : undefined
        }
        action={
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={checking}
            onClick={check}
          >
            {t(
              checking
                ? "settings_finvalda_checking"
                : "settings_finvalda_check"
            )}
          </button>
        }
      />
      {result?.ok && (
        <StatusBanner tone="ok" title={t("settings_finvalda_check_ok")} />
      )}
      {result && !result.ok && (
        <StatusBanner
          tone="error"
          title={t(
            CHECK_FAILURE_TEXT_KEYS[result.reason] ?? CHECK_FAILED_TEXT_KEY
          )}
        />
      )}
    </div>
  );
}

interface RowProps {
  company: CompanyEntity;
  onRefresh: () => Promise<unknown>;
  lock: SaveLock;
}

/** Fetching saves the moment the switch is flipped; if that fails it flips back. */
function SyncToggleRow({ company, onRefresh, lock }: RowProps) {
  const { t } = useTranslation();
  const { save } = useSaveWithToast();
  const [isActive, setIsActive] = useState(company.isFinvaldaSyncActive);
  const isSaved = isFinvaldaConnectionSaved(company);

  const change = async (next: boolean) => {
    setIsActive(next);
    const saved = await lock.hold(() =>
      save(
        async () => {
          await updateFinvaldaSettings(
            { ...storedFinvaldaRequest(company), isFinvaldaSyncActive: next },
            company.id
          );
          await onRefresh();
        },
        { errorMessage: (err) => t(finvaldaSaveErrorKey(err)) }
      )
    );
    if (!saved) setIsActive(!next);
  };

  return (
    <SettingRow
      label={t("finvalda_sync_active")}
      description={t("finvalda_sync_active_hint")}
      value={!isSaved && t("settings_finvalda_save_connection_first")}
      action={
        <Toggle
          checked={isActive}
          onChange={change}
          disabled={lock.isLocked || !isSaved}
          label={t("finvalda_sync_active")}
        />
      }
    />
  );
}

/** The journal group the sales invoices are fetched from. Saving it never needs the password. */
function SalesJournalGroupRow({ company, onRefresh, lock }: RowProps) {
  const { t } = useTranslation();
  const { save, error } = useSaveWithToast();
  const saved = company.finvaldaSalesJournalGroup ?? "";
  const edit = useInlineEdit(saved);
  const isSaved = isFinvaldaConnectionSaved(company);

  // Empty clears the group.
  const group = edit.draft.trim();
  const isInvalid = group !== "" && !JOURNAL_GROUP_FORMAT.test(group);

  const submit = async () => {
    if (isInvalid) return;
    const done = await lock.hold(() =>
      save(
        async () => {
          await updateFinvaldaSettings(
            {
              ...storedFinvaldaRequest(company),
              finvaldaSalesJournalGroup: group,
            },
            company.id
          );
          await onRefresh();
        },
        { errorMessage: (err) => t(finvaldaSaveErrorKey(err)) }
      )
    );
    if (done) edit.cancel();
  };

  return (
    <SettingRow
      label={t("settings_finvalda_journal_group_label")}
      description={t("settings_finvalda_journal_group_description")}
      value={
        isSaved
          ? saved || t("settings_none")
          : t("settings_finvalda_save_connection_first")
      }
      action={isSaved ? <ChangeLink onClick={edit.start} /> : undefined}
    >
      {edit.editing && (
        <EditPanel
          onSave={submit}
          onCancel={edit.cancel}
          saving={lock.isLocked}
          error={
            isInvalid ? t("settings_finvalda_journal_group_invalid") : error
          }
        >
          <label htmlFor="finvalda-journal-group" className={labelCls}>
            {t("settings_finvalda_journal_group_label")}
          </label>
          <input
            id="finvalda-journal-group"
            className={`${inputCls} max-w-xs`}
            value={edit.draft}
            maxLength={40}
            aria-invalid={isInvalid}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              edit.setDraft(event.target.value)
            }
          />
          <p className={helpTextCls}>
            {t("settings_finvalda_journal_group_hint")}
          </p>
        </EditPanel>
      )}
    </SettingRow>
  );
}

interface FinvaldaTabProps {
  companyEntity: CompanyEntity;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

/** The accounting program ERP takes customers, products, invoices and debts from. */
export default function FinvaldaTab({
  companyEntity,
  onRefresh,
}: FinvaldaTabProps) {
  const lock = useSaveLock();
  // A save is done once the server has it: a reload that fails afterwards must
  // not make the page show the save as failed (or flip the switch back).
  const reloadQuietly = () => onRefresh().catch(() => undefined);

  return (
    <div className="space-y-5">
      <SyncStatus company={companyEntity} />
      <SettingsCard>
        <SyncToggleRow
          company={companyEntity}
          onRefresh={reloadQuietly}
          lock={lock}
        />
        <SalesJournalGroupRow
          company={companyEntity}
          onRefresh={reloadQuietly}
          lock={lock}
        />
      </SettingsCard>
      <FinvaldaConnectionCard
        company={companyEntity}
        onRefresh={reloadQuietly}
        lock={lock}
      />
      <LedgerJournalsCard companyId={companyEntity.id} />
    </div>
  );
}
